import { ReloadOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Drawer, Form, Switch, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { DateRangeFilter } from "../../../../../components/Filters/DateRangeFilter.tsx"
import { DebouncedInput } from "../../../../../components/Input/DebouncedInput.tsx"
import {
  LEADS_ADVANCED_FILTER_KEYS,
  countActiveFilters,
  normalizeFilterValue
} from "../../../redux/leads.constants.ts"
import type {
  LeadsFilterOptions,
  LeadsFilters
} from "../../../redux/leads.types.ts"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"

const { Text } = Typography

interface LeadListTableMoreFiltersProps {
  open: boolean
  filters: LeadsFilters
  filterOptions: LeadsFilterOptions
  showConvertedAllowed?: boolean
  /** Bumped by every reset path, including Clear All outside this drawer. */
  resetSignal: number
  onClose: () => void
  onFilersChange: (data: Partial<LeadsFilters>) => void
  onReset: () => void
}

export function LeadListTableMoreFilters({
  open,
  filters,
  filterOptions,
  showConvertedAllowed,
  resetSignal,
  onClose,
  onFilersChange,
  onReset
}: LeadListTableMoreFiltersProps) {
  const { t } = useTranslation()
  const onFilterChange = (key: keyof LeadsFilters) => (value: unknown) => {
    onFilersChange({ [key]: normalizeFilterValue(value) })
  }

  const activeCount = countActiveFilters(filters, LEADS_ADVANCED_FILTER_KEYS)

  return (
    <Drawer
      title={t("leadsTable.filters.drawerTitle")}
      placement="right"
      open={open}
      onClose={onClose}
      width={360}
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined />}
          disabled={!activeCount}
          onClick={onReset}
        >
          Reset
        </Button>
      }
    >
      <Form layout="vertical">
        <Form.Item label={t("filters.customerLabel")}>
          <DebouncedInput
            resetSignal={resetSignal}
            prefix={<SearchOutlined />}
            placeholder={t("filters.customerSearchPlaceholder")}
            value={filters?.customer_name}
            onValueChange={(value) => onFilersChange({ customer_name: value })}
          />
        </Form.Item>
        <Form.Item label={t("leadsTable.filters.dateCreated")}>
          <DateRangeFilter
            after={filters?.created_after}
            before={filters?.created_before}
            afterKey="created_after"
            beforeKey="created_before"
            placeholder={t("filters.anyDate")}
            className={"w-full"}
            onChange={onFilersChange}
          />
        </Form.Item>
        <Form.Item label={t("leadsTable.filters.projectType")}>
          <ResolvedSelect
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("leadsTable.filters.allProjectTypes")}
            value={filters?.project_type}
            options={filterOptions?.["project_type"]}
            onChange={onFilterChange("project_type")}
          />
        </Form.Item>
        <Form.Item label={t("leadsTable.filters.state")}>
          <ResolvedSelect
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("leadsTable.filters.allStates")}
            value={filters?.state}
            options={filterOptions?.["state"]}
            onChange={onFilterChange("state")}
          />
        </Form.Item>
        <Form.Item label={t("leadsTable.filters.contactMethod")}>
          <ResolvedSelect
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("leadsTable.filters.anyContactMethod")}
            value={filters?.preferred_contact_method}
            options={filterOptions?.["contact_method"]}
            onChange={onFilterChange("preferred_contact_method")}
          />
        </Form.Item>
        {showConvertedAllowed && (
          <Form.Item
            label={t("leadsTable.filters.showConverted")}
            tooltip="Leads that became projects. They are managed from Projects and cannot be edited here."
          >
            <Switch
              checked={!!filters?.show_converted}
              onChange={(checked) =>
                onFilersChange({ show_converted: checked || null })
              }
            />
            <Text type="secondary" className="block mt-1">
              Off by default — converted leads are hidden from this list.
            </Text>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  )
}
